import { apiInternals, send } from './client';

/**
 * `request()` is not itself exported from `client.ts` — only `send()` and
 * `apiInternals` are. `apiInternals.request` is the one non-`send()` path a
 * production module outside `client.ts` may use, and only for the reason
 * `api.auditLog` (and `codes.ts`'s `codesApi.list`) state there: reading
 * `meta` as well as `data`.
 */
const { request } = apiInternals;

/**
 * The case register (M-06, Task 4) — create/list/read/update/close a support
 * case, and claim a dispensary code per requested product line at create
 * time (M-05's `App\Support\Code\CodeClaim`, first exercised here).
 *
 * ## `CaseRow` is verified against `CaseResource`, not the task brief's prose
 *
 * `app/Http/Resources/Cases/CaseResource.php` in the API repository is the
 * ONE shape both `GET /cases` and `GET /cases/{uuid}` answer — there is no
 * separate detail variant, unlike `Sensor`/`SensorWithTrail`. `dispensary` is
 * `{uuid, name_en}` (or `null`; a case always carries a dispensary, so this
 * key is `null` only when the relation is not loaded, which every read path
 * here always loads), `city` is `{uuid, name_ar}` — the ARABIC label, the one
 * relation on this resource that surfaces `name_ar` rather than `name_en` —
 * and `centre` is `{uuid, name_en}`; `city`/`centre` are independently
 * nullable, since `city_id`/`center_id` are nullable columns. `type` is the
 * comma-joined STRING `CaseRecord.type` stores (`'Reader,Sensor'`), never an
 * array — `StoreCaseRequest::caseAttributes()` is what joins the wire's
 * array into this string server-side, and nothing on the read side splits
 * it back.
 *
 * ## `list` is built on `request`, never `send`
 *
 * `GET /cases` is CURSOR-PAGINATED — `{ data, meta: { next_cursor,
 * per_page } }`, `CasePage`'s own shape, exactly `CodePage`/`AuditPage`'s
 * shape. `send()` resolves to `request()`'s `.data` ALONE and throws `meta`
 * away; a `list` written as `send<CasePage>(...)` would compile (the cast
 * inside `request()` is unchecked) but resolve to a bare `CaseRow[]` with no
 * `.meta` on it at all — the UI's "next page" control would have nothing to
 * read and would simply never appear, with no error anywhere. See
 * `codes.ts`'s own docblock and M-01c's report for this exact defect shipped
 * once already.
 *
 * ## `create`'s response carries `code_claims` alongside `data`
 *
 * `POST /cases` answers ONE ENTRY PER REQUESTED PRODUCT LINE
 * (`CaseController::codeClaims()`), each naming whether a dispensary code
 * was actually claimed for that line and, if so, which. AN UNCLAIMED LINE IS
 * NOT AN ERROR — the pool may hold nothing for that dispensary and type
 * right now (`App\Support\Code\CodeClaim::claim()` returning `null` means
 * "nothing takeable right now", not "the pool is empty"), and the case is
 * created regardless. `CodeClaimResult.code` is therefore `string | null`,
 * never assumed present alongside `claimed: true` by construction — a screen
 * reads `claimed` to decide whether to render a code or an empty-pool
 * notice, not the presence of `code` alone.
 *
 * ## `409` is distinguishable from `422` on `update`/`close`
 *
 * `PATCH /cases/{uuid}` answers `409` when the case is already closed;
 * `POST /cases/{uuid}/close` answers `409` on a second close. Both are
 * ordinary `ApiError`s — the caller distinguishes them by `error.status`
 * (`client.ts`'s own `ApiError`), never by `code` alone, the identical split
 * `codes.ts`'s `remove()` documents for its own `409` (a bound code) versus a
 * duplicate-on-create `422`.
 *
 * ## `type` on `CaseQuery`/`CaseInput` is the request array, not the stored string
 *
 * `StoreCaseRequest.type` is `('Reader' | 'Sensor')[]` on the wire IN — the
 * transcribed casing `StoreCaseRequest`'s own docblock describes, not
 * `DispensaryCode.kind`'s lower-case vocabulary. `CaseInput.type` mirrors
 * that array shape exactly; it is NOT the same type as `CaseRow.type` (a
 * joined string), because the two travel in opposite directions across two
 * different endpoints and happen to share a name only in English.
 *
 * ## `CaseInput` carries `perception_code`/`assessment_code`/`agent_notes`
 * (M-06 fix round, gap 1)
 *
 * `StoreCaseRequest::rules()` (verified against the API repository AFTER
 * this fix) now accepts all three, `sometimes`/`nullable` — matching
 * exactly what `CaseUpdateInput` already carries for the same three
 * fields. `create()` sends them on the SAME request as everything else;
 * there is no follow-up `update()` call for them any more.
 *
 * ## `CaseRow.code_claims` is present on `GET /cases/{uuid}` only (M-06 fix
 * round, gap 2)
 *
 * `CaseResource` (verified against the API repository AFTER this fix) now
 * carries a `code_claims` key, but ONLY when its `codes` relation was
 * eager-loaded — `CaseController::show()` loads it, `index()` deliberately
 * does not (a list of cases each carrying its own codes is a heavier
 * response for no shown benefit). `CaseRow.code_claims` is therefore
 * OPTIONAL (`?:`), present on a detail fetch and absent on a list row —
 * never assumed present by construction. Every entry reports a code this
 * case has ACTUALLY claimed (`claimed` is always `true` here) — unlike
 * `CaseCreateResult.code_claims`, there is no unclaimed-line entry,
 * because nothing on `cases` records which lines a past request asked
 * for, only which vouchers landed; the shape is otherwise identical
 * (`CodeClaimResult`), reused rather than redeclared.
 */

/** `CaseResource`'s `dispensary` key — `uuid` + `name_en`, never the integer foreign key. */
export interface CaseDispensaryRef {
  uuid: string;
  name_en: string;
  name_ar: string;
}

/**
 * `CaseResource`'s `city` key.
 *
 * BOTH NAMES, AND THAT IS NEW. This carried `name_ar` alone while its two
 * siblings carried `name_en` alone, so no screen could render a case's
 * hierarchy in one language. Nobody noticed because the seeded data was
 * wrong in the mirror-image way — `cities.name_en` held the Arabic name —
 * so reading the one available column happened to look right until the
 * reference data was corrected. Use `referenceName()`, never a bare field.
 */
export interface CaseCityRef {
  uuid: string;
  name_en: string;
  name_ar: string;
}

/** `CaseResource`'s `centre` key — `uuid` + both names, see `CaseCityRef`. */
export interface CaseCentreRef {
  uuid: string;
  name_en: string;
  name_ar: string;
}

/** Every status a case may hold, `CaseIndexRequest`'s own filter enum — includes `closed`, unlike `UpdateCaseRequest`'s. */
export type CaseStatus = 'open' | 'in_progress' | 'waiting' | 'escalated' | 'closed' | 'cancelled';

/** What the pharmacist hands over, recorded as the case closes. */
export interface CaseCloseInput {
  replacement_reader_serial?: string | null;
  replacement_sensor_serial?: string | null;
}

/**
 * One case, exactly as `GET /cases` and `GET /cases/{uuid}` both render it —
 * verified field-for-field against `CaseResource.php`, not inferred from the
 * task brief's prose. See this module's own docblock for `type`'s string
 * shape and `city`'s Arabic label.
 */
export interface CaseRow {
  uuid: string;
  case_number: string;
  mobile: string;
  perception_code: string | null;
  assessment_code: string | null;
  /** Comma-joined, e.g. `'Reader,Sensor'` — never an array. See this module's own docblock. */
  type: string;
  status: CaseStatus;
  dispensary: CaseDispensaryRef | null;
  city: CaseCityRef | null;
  centre: CaseCentreRef | null;
  agent_notes: string | null;
  /**
   * M-08 Task 2 — M-06's K-9's missing columns: the serial the agent typed
   * on the device in hand, and the `device_models.code` naming its
   * generation. FREE TEXT, LINKING NOTHING (`CaseRecord`'s own class
   * docblock) — always present on the resource (never `whenLoaded()`), so
   * each is `string | null` here, never optional.
   */
  reader_serial: string | null;
  reader_type: string | null;
  sensor_serial: string | null;
  sensor_type: string | null;
  /**
   * Present on `GET /cases/{uuid}` only, absent from `GET /cases` rows —
   * see this module's own docblock. Every entry's `claimed` is `true`;
   * `CodeClaimResult` is reused rather than redeclared.
   */
  code_claims?: readonly CodeClaimResult[];
  /** ISO 8601, or `null` while the case is still open. */
  closed_at: string | null;
  /** The device the patient LEFT with. Null until the case is closed. */
  replacement_reader_serial: string | null;
  replacement_sensor_serial: string | null;
  created_at: string | null;
}

/**
 * Every filter `GET /cases` accepts, `CaseIndexRequest`'s own rules. All
 * optional; an empty query lists every case the caller may see.
 */
export interface CaseQuery {
  case_number?: string;
  mobile?: string;
  status?: CaseStatus;
  /** The dispensary's `uuid`, never the integer foreign key. */
  dispensary?: string;
  from?: string;
  to?: string;
  /**
   * What the patient LEFT with. `reader`/`sensor` each require that serial
   * to be set, `any` either, `none` neither.
   *
   * Four answers rather than a serial box: the question asked of this list
   * is "which cases delivered a device", and a serial already finds its own
   * case through `case_number`/`mobile`. Reader and sensor are separate
   * because the volumes are nothing alike — 137,670 migrated cases name a
   * replacement sensor against 11,610 naming a reader.
   */
  replacement?: 'any' | 'none' | 'reader' | 'sensor';
  cursor?: string;
  per_page?: number;
}

/** One page of the register — cursor-paginated, exactly `CodePage`/`AuditPage`'s shape. */
export interface CasePage {
  data: readonly CaseRow[];
  meta: {
    next_cursor: string | null;
    per_page: number;
    /**
     * How many cases match the query FILTERS within what the caller may
     * see — not how many are on this page, and not how many remain after
     * the cursor. Unaffected by `cursor` and `per_page`, so a caller that
     * wants only the count asks for `per_page: 1` and reads this.
     *
     * The one thing a cursor-paginated endpoint otherwise cannot answer,
     * and the home screen's open-cases tile displayed a hard-coded literal
     * for want of it.
     */
    total: number;
  };
}

/**
 * `POST /cases`'s body, `StoreCaseRequest`'s own field names exactly.
 * `type` IS THE TRANSCRIBED-CASING ARRAY (`'Reader'`/`'Sensor'`), never
 * `DispensaryCode.kind`'s lower-case vocabulary — see this module's own
 * docblock. `reader_type_code`/`sensor_type_code` are each required by the
 * API only when the matching `type` entry is present; both are optional
 * here because a caller requesting only one line never sends the other.
 * `dispensary_id`/`city_id`/`center_id` are each the target's `uuid`, never
 * the integer foreign key — `StoreCaseRequest`'s own convention.
 */
export interface CaseInput {
  case_number: string;
  mobile: string;
  dispensary_id: string;
  city_id?: string;
  center_id?: string;
  type: readonly ('Reader' | 'Sensor')[];
  reader_type_code?: string;
  sensor_type_code?: string;
  /**
   * `StoreCaseRequest::rules()` (verified against the API repository AFTER
   * M-06's fix round, gap 1) — `sometimes`/`nullable`, matching
   * `CaseUpdateInput`'s own three fields exactly. Sent on the SAME create
   * call as everything else; there is no follow-up `update()` for these.
   */
  perception_code?: string | null;
  assessment_code?: string | null;
  agent_notes?: string | null;
  /**
   * M-08 Task 2 — `sometimes`/`nullable` server-side (`StoreCaseRequest::
   * rules()`, verified against the API repository) and optional here for
   * the same reason: a case reporting only one line never sends the
   * other's serial.
   *
   * NO `reader_type`/`sensor_type` HERE (fix round 1) — `StoreCaseRequest`
   * carries no rule for either; the API derives the stored generation
   * from `reader_type_code`/`sensor_type_code` above, because the
   * generation a voucher is claimed against IS the patient's device
   * generation, and a second, independent field for the same fact could
   * disagree with it. See that class's own docblock. `CaseUpdateInput`
   * below is different — an edit claims no voucher, so it keeps both.
   */
  reader_serial?: string;
  sensor_serial?: string;
}

/**
 * `PATCH /cases/{uuid}`'s body, `UpdateCaseRequest`'s own field names.
 * `status` EXCLUDES `'closed'` FROM ITS OWN ENUM — sending it here is a
 * plain `422`; only `POST /cases/{uuid}/close` may ever close a case. Every
 * field is optional (`sometimes` server-side): a caller sends only what it
 * means to change.
 */
export interface CaseUpdateInput {
  agent_notes?: string | null;
  perception_code?: string | null;
  assessment_code?: string | null;
  status?: Exclude<CaseStatus, 'closed'>;
  city_id?: string | null;
  center_id?: string | null;
  /**
   * M-08 Task 2 — an update can add a serial to a line left blank at
   * create time, or correct a generation typed wrong the first time. Same
   * rule shape as `StoreCaseRequest`'s own four.
   */
  reader_serial?: string | null;
  reader_type?: string | null;
  sensor_serial?: string | null;
  sensor_type?: string | null;
}

/**
 * One entry of `POST /cases`'s `code_claims`, one per requested product
 * line — `CaseController::codeClaims()`'s own shape. `claimed: false` (and
 * `code: null`) is a NORMAL outcome, not a failure: the pool held nothing
 * for that dispensary and type at the moment of the request.
 */
export interface CodeClaimResult {
  kind: 'reader' | 'sensor';
  type_code: string;
  claimed: boolean;
  code: string | null;
}

/** `POST /cases`'s full response — the created case, and one claim outcome per requested line. */
export interface CaseCreateResult {
  data: CaseRow;
  code_claims: readonly CodeClaimResult[];
}

/**
 * `GET /cases/summary`'s response (M-15b Task 1) — case-status tiles and two
 * breakdowns for the `/reports` dashboard. `by_status` always carries all
 * six `CaseStatus` keys, even at zero. `by_closer`/`by_city` are each sorted
 * by `count` descending and omit zero-count rows; `by_city`'s null-city
 * bucket carries `uuid`, `name_en` AND `name_ar` all `null` — the caller
 * supplies the "Unspecified" label itself, the server never translates it.
 *
 * ## `by_closer` is OPTIONAL (final whole-branch review, fix 1)
 *
 * The by-closer breakdown names individual staff members' own case-closure
 * counts, which is narrower than `case.view` (all five seeded roles) — it is
 * gated on `user.view` (administrator and supervisor only). When the caller
 * lacks `user.view`, the server OMITS the `by_closer` KEY ENTIRELY, never an
 * empty array: an empty array is indistinguishable from "nobody closed a
 * case in this range", which is a real, distinct answer this endpoint must
 * still be able to give to a caller who DOES hold the permission. A caller
 * must therefore branch on the key's PRESENCE (`'by_closer' in summary`, or
 * an `undefined` check), never render an empty state for its absence, and
 * never assume the key exists by construction the way `by_status`/`by_city`
 * still may.
 */
/** A named thing and how many cases it accounts for. */
export interface SummaryNamedCount {
  uuid: string | null;
  name_en: string | null;
  name_ar: string | null;
  count: number;
}

/**
 * `GET /cases/summary` (M-15b, extended by M-20 into the dashboard's whole
 * data source).
 *
 * FOUR BLOCKS ARE OPTIONAL, AND EACH ONE'S ABSENCE MEANS "you may not see
 * this", never "there is none". The server omits `by_closer` without
 * `user.view`, `sms` without `sms.view`, `replacements` without
 * `replacement.view` and `escalations` without an escalation permission —
 * deliberately omitting the KEY rather than sending an empty value, because
 * an empty value is a real and reachable state for a permitted caller.
 * Render the panel only when its key is present; never coalesce to `{}`.
 */
export interface CaseSummary {
  totals: {
    total: number;
    /** Every case that is neither closed nor cancelled — NOT `by_status.open`. */
    open: number;
    closed: number;
    /** `null` when nothing in range has been closed. Never coalesce to 0. */
    average_days_to_close: number | null;
  };
  by_status: Record<CaseStatus, number>;
  by_closer?: readonly { uuid: string; full_name: string; count: number }[];
  by_city: readonly SummaryNamedCount[];
  by_dispensary: readonly SummaryNamedCount[];
  /** The RAW comma-joined `cases.type`, e.g. `'Reader,Sensor'`. */
  by_type: readonly { type: string; count: number }[];
  /** One entry per day that saw an opening or a closure. Gaps are real. */
  daily: readonly { date: string; opened: number; closed: number }[];
  sms?: Readonly<Record<string, number>>;
  /**
   * EVERY replacement, legacy and new. Legacy's own live here as `dispensed`
   * (the close was the dispense), so these figures span the whole history
   * rather than starting at cutover.
   */
  replacements?: {
    by_status: Readonly<Record<string, number>>;
    /** `reader` / `sensor`. The split is wide — a sensor is the common one. */
    by_device_type: Readonly<Record<string, number>>;
  };
  escalations?: {
    by_status: Readonly<Record<string, number>>;
    by_level: Readonly<Record<string, number>>;
  };
  /**
   * Complaints raised against staff. NOT case-scoped — a complaint is about
   * a person — so a caller confined to a dispensary counts only their own
   * branch's staff, and the date range filters when it HAPPENED rather than
   * when somebody typed it in.
   */
  justifications?: {
    by_status: Readonly<Record<string, number>>;
    by_category: Readonly<Record<string, number>>;
  };
}

/**
 * `?key=value&...`, or `''` when every value is absent. `undefined` is
 * dropped rather than becoming the literal string `'undefined'` in the URL —
 * the identical helper `codes.ts`/`client.ts` each redeclare locally for the
 * same reason (neither is exported).
 */
function queryString(params: object): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params) as [string, string | number | undefined][]) {
    if (value === undefined) continue;
    search.set(key, String(value));
  }
  const built = search.toString();
  return built === '' ? '' : `?${built}`;
}

export const casesApi = {
  /**
   * DELIBERATELY NOT `send<CasePage>()` — see this module's own docblock for
   * why that would compile and still lose `meta.next_cursor`.
   */
  list: async (query: CaseQuery = {}): Promise<CasePage> => {
    const { data, meta } = await request<readonly CaseRow[]>(`/cases${queryString(query)}`);
    return { data, meta: meta as CasePage['meta'] };
  },

  show: (uuid: string) => send<CaseRow>(`/cases/${uuid}`),

  /**
   * Returns `data` AND `code_claims` — never `send<CaseRow>()`, which would
   * compile and silently discard which lines got a code, and never
   * `request()`'s own `meta` either: `code_claims` is a SIBLING of `data` in
   * the response (`CaseController::store()`'s `->additional(['code_claims'
   * => ...])`), not nested under `meta` the way `list()`'s pagination is —
   * `envelope` is the whole parsed body, read for exactly this reason (see
   * `client.ts`'s own `ApiResponse` docblock).
   */
  create: async (input: CaseInput): Promise<CaseCreateResult> => {
    const { data, envelope } = await request<CaseRow>('/cases', { method: 'POST', body: input });
    const body = envelope as { code_claims?: readonly CodeClaimResult[] } | undefined;
    return { data, code_claims: body?.code_claims ?? [] };
  },

  /**
   * `409` when the case is already closed — distinguishable from a `422` by
   * `error.status` on the thrown `ApiError`, never by `code` alone. See this
   * module's own docblock.
   */
  update: (uuid: string, input: CaseUpdateInput) =>
    send<CaseRow>(`/cases/${uuid}`, { method: 'PATCH', body: input }),

  /**
   * `409` on a second close of the same case — distinguishable from a `422`
   * by `error.status`, the identical split `update()` documents.
   */
  /**
   * Close a case, recording the device handed over.
   *
   * Both serials are optional and a case may close with neither — the
   * patient never came, or the closer is an agent tidying up rather than a
   * pharmacy dispensing. Omitting a key leaves whatever is stored; sending
   * `null` clears it.
   *
   * CLOSING ALSO MOVES THE CASE to the closer's dispensary when they are
   * confined to one, which is what the legacy close does: the case belongs
   * to the branch that actually served the patient.
   */
  close: (uuid: string, devices?: CaseCloseInput) =>
    send<CaseRow>(`/cases/${uuid}/close`, { method: 'POST', body: devices ?? {} }),

  /**
   * The close, undone. Requires `case.reopen` — a SEPARATE permission from
   * `case.close`, administrator-only: four roles may end a case, and putting
   * a finished one back into every queue and open-case count is not the same
   * grant.
   *
   * `409` when the case is not closed, the mirror of `close()`'s own `409`
   * and distinguishable the same way, by `error.status`.
   */
  reopen: (uuid: string) => send<CaseRow>(`/cases/${uuid}/reopen`, { method: 'POST' }),

  /**
   * `DELETE /cases/{case}` (M-20) — a case created in error.
   *
   * Requires `case.delete`, which the administrator role holds and no other
   * role does; a caller without it gets a `403` and a caller who cannot see
   * the case at all gets a `404`, never a `403` that would confirm it
   * exists. Soft on the server, and it RELEASES the case's claimed
   * dispensary codes back into the pool — which is why the confirmation
   * dialog says so rather than asking a bare "are you sure".
   */
  remove: (uuid: string) => send<void>(`/cases/${uuid}`, { method: 'DELETE' }),

  /**
   * `GET /cases/summary` (M-15b Task 1) — the `/reports` dashboard's one
   * data source. `send()` is correct here (unlike `list()`): this endpoint
   * carries no `meta`, so nothing is lost by discarding the envelope.
   */
  summary: (params: { from?: string; to?: string } = {}) =>
    send<CaseSummary>(`/cases/summary${queryString(params)}`),
};
